import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { Facility } from "../../models/facility.model";
import { facilityService } from "../../service/facilites.service";
import PageHeader from "../PageHeader";

const initialFacility: Partial<Facility> = {
  name: "",
  description: "",
  iconName: "",
};

const FacilityComponent: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [facility, setFacility] = useState<Partial<Facility>>(initialFacility);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("facilityTablePage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("facilityTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortField, setSortField] = useState(() => {
    const savedSort = localStorage.getItem("facilityTableSortField");
    return savedSort || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | -1>(() => {
    const savedOrder = localStorage.getItem("facilityTableSortOrder");
    return savedOrder ? (parseInt(savedOrder) as 1 | -1) : 1;
  });

  useEffect(() => {
    const fetchFacilities = async () => {
      setListLoading(true);
      try {
        const result = await facilityService.getAllPaginated({
          pageNumber: page,
          pageSize: rows,
          sortField,
          sortOrder: sortOrder === -1 ? "desc" : "asc",
          globalFilter,
        });
        setFacilities(
          (result?.items ?? []).map((item: any) => ({
            ...item,
            id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
          }))
        );
        setTotalRecords(result?.totalCount ?? 0);
      } catch {
        setFacilities([]);
        setTotalRecords(0);
      } finally {
        setListLoading(false);
      }
    };
    fetchFacilities();
  }, [success, page, rows, sortField, sortOrder, globalFilter, refreshKey]);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFacility({ ...facility, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      if (facility.id) {
        const updated = await facilityService.update(
          facility.id,
          facility as Facility
        );
        setFacilities((prev) =>
          prev.map((f) => (f.id === facility.id ? updated : f))
        );
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Facility updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      } else {
        const created = await facilityService.create(facility as Facility);
        setFacilities((prev) => [...prev, created]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Facility created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      }
      setFacility(initialFacility);
      setShowCreateForm(false);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err.message || "Error saving facility"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row: Facility) => {
    setFacility(row);
    setShowCreateForm(true);
  };

  const handleDelete = async (row: Facility) => {
    if (!row.id) return;
    try {
      await facilityService.delete(row.id);
      setSuccess(true);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Facility deleted successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch {
      setError("Failed to delete facility");
    }
  };

  const actionBodyTemplate = (rowData: Facility) => (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleEdit(rowData)}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleDelete(rowData)}
        title="Delete"
        style={{ color: "#dc3545" }}
      >
        <Icon.Trash size={20} />
      </Button>
    </div>
  );

  return (
    <div className="content">
      <PageHeader
        title="Facility List"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Facilities"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setPage(1);
                      localStorage.setItem("facilityTablePage", "1");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button
                  className="btn btn-primary ms-2"
                  style={{ minWidth: 160 }}
                  onClick={() => {
                    setFacility(initialFacility);
                    setShowCreateForm(true);
                  }}
                >
                  <Icon.PlusCircle className="me-1" /> Create Facility
                </Button>
              </div>

              <DataTable
                value={facilities}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  setPage(newPage);
                  setRows(e.rows);
                  localStorage.setItem("facilityTablePage", newPage.toString());
                  localStorage.setItem("facilityTableRows", e.rows.toString());
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem("facilityTableSortField", newSortField);
                  localStorage.setItem(
                    "facilityTableSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No facilities found."
                className="grid-table"
              >
                {/* <Column field="id" header="Id" sortable /> */}
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                {/* <Column field="iconName" header="Icon Name" sortable /> */}
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showCreateForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {facility.id ? "Update Facility" : "Create Facility"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowCreateForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name:</label>
                            <input
                              className="form-control"
                              name="name"
                              value={facility.name || ""}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description:</label>
                            <textarea
                              className="form-control"
                              name="description"
                              value={facility.description || ""}
                              onChange={handleChange}
                            />
                          </div>
                          {/* <div className="mb-3">
                            <label className="form-label">Icon Name:</label>
                            <input className="form-control" name="iconName" value={facility.iconName || ""} onChange={handleChange} required />
                          </div> */}
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? facility.id
                                ? "Updating..."
                                : "Creating..."
                              : facility.id
                              ? "Update Facility"
                              : "Create Facility"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCreateForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityComponent;
export { FacilityComponent };
